import { _ as __rest } from "./tslib.es6.js";
import React__default, { useRef, useState, useMemo, useEffect } from "react";
import classNames from "classnames";
import { D as DataContext } from "./context4.js";
import { g as getRect } from "./use-client-rect.js";
import { I as Indicator } from "./indicator2.js";
import { u as useTouch } from "./use-touch.js";
import { r as requestAniFrame } from "./raf.js";
import SwiperItem from "./SwiperItem.js";
const defaultProps = {
  duration: 500,
  defaultValue: 0,
  autoPlay: 0,
  direction: "horizontal",
  indicator: false,
  loop: false,
  touchable: true,
  preventDefault: true,
  stopPropagation: true,
  center: false,
  className: ""
};
const Swiper = React__default.forwardRef((props, ref) => {
  const mergedProps = Object.assign(Object.assign({}, defaultProps), props);
  const { children, direction, className, onChange, defaultValue, indicator, touchable, preventDefault, stopPropagation, autoPlay, center } = mergedProps, rest = __rest(mergedProps, ["children", "direction", "className", "onChange", "defaultValue", "indicator", "touchable", "preventDefault", "stopPropagation", "autoPlay", "center"]);
  const container = useRef(null);
  const innerRef = useRef(null);
  const swiperRef = useRef({
    moving: false,
    autoplayTimer: null,
    width: 0,
    height: 0,
    offset: 0,
    size: 0
  });
  const touch = useTouch();
  const isVertical = direction === "vertical";
  const [rect, setRect] = useState(null);
  let [active, setActive] = useState(0);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [offset, setOffset] = useState(0);
  const [childOffset, setChildOffset] = useState([]);
  const [ready, setReady] = useState(false);
  let size = isVertical ? height : width;
  const { swiperItems, swiperItemCount } = useMemo(() => {
    let count = 0;
    const swiperItems2 = React__default.Children.map(props.children, (child) => {
      if (!React__default.isValidElement(child))
        return null;
      count++;
      return child;
    });
    return {
      swiperItems: swiperItems2,
      swiperItemCount: count
    };
  }, [props.children]);
  let trackSize = swiperItemCount * Number(size);
  const parent = {
    propSwiper: mergedProps,
    size
  };
  const minOffset = (() => {
    if (rect) {
      const base = isVertical ? rect.height : rect.width;
      return base - Number(size) * swiperItemCount;
    }
    return 0;
  })();
  const stopAutoPlay = () => {
    clearTimeout(swiperRef.current.autoplayTimer);
    swiperRef.current.autoplayTimer = null;
  };
  const startPlay = () => {
    if (mergedProps.autoPlay <= 0 || swiperItemCount <= 1)
      return;
    stopAutoPlay();
    swiperRef.current.autoplayTimer = setTimeout(() => {
      next();
      startPlay();
    }, Number(mergedProps.autoPlay));
  };
  const resetPosition = () => {
    swiperRef.current.moving = true;
    if (active <= -1) {
      move({ pace: swiperItemCount });
    }
    if (active >= swiperItemCount) {
      move({ pace: -swiperItemCount });
    }
  };
  const prev = () => {
    resetPosition();
    touch.reset();
    requestAniFrame(() => {
      requestAniFrame(() => {
        swiperRef.current.moving = false;
        move({
          pace: -1,
          isEmit: true
        });
      });
    });
  };
  const next = () => {
    resetPosition();
    touch.reset();
    requestAniFrame(() => {
      requestAniFrame(() => {
        swiperRef.current.moving = false;
        move({
          pace: 1,
          isEmit: true
        });
      });
    });
  };
  const to = (index) => {
    resetPosition();
    touch.reset();
    requestAniFrame(() => {
      requestAniFrame(() => {
        swiperRef.current.moving = false;
        let targetIndex;
        if (props.loop && swiperItemCount === index) {
          targetIndex = active === 0 ? 0 : index;
        } else {
          targetIndex = index % swiperItemCount;
        }
        move({
          pace: targetIndex - active,
          isEmit: true
        });
      });
    });
  };
  const resize = () => {
    init(active);
  };
  const move = ({ pace = 0, offset: offset2 = 0, isEmit = false, movingStatus = false }) => {
    var _a;
    if (swiperItemCount <= 1)
      return;
    const targetActive = getActive(pace);
    const targetOffset = getOffset(targetActive, offset2);
    if (props.loop) {
      if (Array.isArray(children) && children[0] && targetOffset !== minOffset) {
        const rightBound = targetOffset < minOffset;
        childOffset[0] = rightBound ? trackSize : 0;
      }
      if (Array.isArray(children) && children[swiperItemCount - 1] && targetOffset !== 0) {
        const leftBound = targetOffset > 0;
        childOffset[swiperItemCount - 1] = leftBound ? -trackSize : 0;
      }
      setChildOffset(childOffset);
    }
    if (isEmit && active !== targetActive) {
      (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, (targetActive + swiperItemCount) % swiperItemCount);
    }
    active = targetActive;
    setActive(targetActive);
    setOffset(targetOffset);
    getStyle(targetOffset);
  };
  React__default.useImperativeHandle(ref, () => ({
    to,
    next,
    prev,
    resize
  }));
  const getActive = (pace) => {
    if (pace) {
      const _active = active + pace;
      if (props.loop) {
        return range(_active, -1, swiperItemCount);
      }
      return range(_active, 0, swiperItemCount - 1);
    }
    return active;
  };
  const getOffset = (active2, offset2 = 0) => {
    let currentPosition = active2 * Number(size);
    if (!props.loop) {
      currentPosition = Math.min(currentPosition, -minOffset);
    }
    let targetOffset = offset2 - currentPosition;
    if (!props.loop) {
      targetOffset = range(targetOffset, minOffset, 0);
    }
    return targetOffset;
  };
  const range = (num, min, max) => {
    return Math.min(Math.max(num, min), max);
  };
  const classPrefix = "nut-swiper";
  const contentClass = classNames({
    [`${classPrefix}-inner`]: true,
    [`${classPrefix}-vertical`]: isVertical
  });
  const getStyle = (moveOffset = offset) => {
    const target = innerRef.current;
    if (!target)
      return;
    let _offset = 0;
    if (!center) {
      _offset = moveOffset;
    } else {
      const _size = isVertical ? height : width;
      const val = isVertical ? rect.height - _size : rect.width - _size;
      _offset = moveOffset + (active === swiperItemCount - 1 && !props.loop ? -val / 2 : val / 2);
    }
    target.style.transitionDuration = `${swiperRef.current.moving ? 0 : props.duration}ms`;
    target.style[isVertical ? "height" : "width"] = `${Number(size) * swiperItemCount}px`;
    target.style[isVertical ? "width" : "height"] = `${isVertical ? width : height}px`;
    target.style.transform = `translate3D${!isVertical ? `(${_offset}px,0,0)` : `(0,${_offset}px,0)`}`;
  };
  const onTouchStart = (e) => {
    if (!props.touchable)
      return;
    touch.start(e);
    stopAutoPlay();
    resetPosition();
  };
  const onTouchMove = (e) => {
    if (props.preventDefault)
      e.preventDefault();
    if (props.stopPropagation)
      e.stopPropagation();
    if (props.touchable && swiperRef.current.moving) {
      touch.move(e);
      if (touch.direction.current === props.direction) {
        move({
          offset: touch.delta.current
        });
      }
    }
  };
  const onTouchEnd = (e) => {
    if (!props.touchable || !swiperRef.current.moving)
      return;
    const speed = touch.delta.current / (Date.now() - touch.touchTime.current);
    const isShouldMove = Math.abs(speed) > 0.3 || Math.abs(touch.delta.current) > Number((size / 2).toFixed(2));
    swiperRef.current.moving = false;
    if (isShouldMove && touch.direction.current === props.direction) {
      let pace = 0;
      const offset2 = touch.isVertical() ? touch.offsetY : touch.offsetX;
      if (props.loop) {
        if (offset2.current > 0) {
          pace = touch.delta.current > 0 ? -1 : 1;
        }
      } else {
        pace = -Math[touch.delta.current > 0 ? "ceil" : "floor"](touch.delta.current / size);
      }
      move({
        pace,
        isEmit: true
      });
    } else if (touch.delta.current) {
      move({ pace: 0 });
    } else {
      getStyle();
    }
    startPlay();
  };
  const getItemStyle = (index) => {
    const style = {};
    if (size) {
      style[direction === "horizontal" ? "width" : "height"] = `${size}px`;
    }
    const offset2 = childOffset[index];
    if (offset2) {
      style.transform = `translate3D${direction === "horizontal" ? `(${offset2}px,0,0)` : `(0,${offset2}px,0)`}`;
    }
    return style;
  };
  const init = (index) => {
    const rect2 = getRect(container === null || container === void 0 ? void 0 : container.current);
    const currentIndex = Math.max(Math.min(swiperItemCount - 1, index || Number(mergedProps.defaultValue)), 0);
    const width2 = Number(mergedProps.width) || rect2.width;
    const height2 = Number(mergedProps.height) || rect2.height;
    size = isVertical ? height2 : width2;
    trackSize = swiperItemCount * Number(size);
    const targetOffset = getOffset(currentIndex);
    swiperRef.current.moving = true;
    if (ready) {
      swiperRef.current.moving = false;
    }
    setRect(rect2);
    setActive(currentIndex);
    setWidth(width2);
    setHeight(height2);
    setOffset(targetOffset);
    setReady(true);
  };
  useEffect(() => {
    if (ready) {
      getStyle();
    }
  }, [isVertical, width, height, offset, ready]);
  useEffect(() => {
    if (ready) {
      stopAutoPlay();
      startPlay();
    }
    return () => setReady(false);
  }, [ready]);
  useEffect(() => {
    const events = [
      { name: "touchstart", e: onTouchStart },
      { name: "touchmove", e: onTouchMove },
      { name: "touchend", e: onTouchEnd }
    ];
    events.forEach((item) => {
      var _a;
      (_a = container.current) === null || _a === void 0 ? void 0 : _a.addEventListener(item.name, item.e, false);
    });
    return () => {
      events.forEach((item) => {
        var _a;
        (_a = container.current) === null || _a === void 0 ? void 0 : _a.removeEventListener(item.name, item.e, false);
      });
    };
  });
  useEffect(() => init(), [props.defaultValue]);
  useEffect(() => {
    return () => stopAutoPlay();
  }, []);
  const renderIndicator = () => {
    if (React__default.isValidElement(indicator))
      return indicator;
    if (!indicator)
      return null;
    return React__default.createElement(
      "div",
      { className: classNames({
        [`${classPrefix}-indicator`]: true,
        [`${classPrefix}-indicator-vertical`]: isVertical
      }) },
      React__default.createElement(Indicator, { current: (active + swiperItemCount) % swiperItemCount, total: swiperItems === null || swiperItems === void 0 ? void 0 : swiperItems.length, direction })
    );
  };
  return React__default.createElement(
    DataContext.Provider,
    { value: parent },
    React__default.createElement(
      "div",
      Object.assign({ className: classNames(classPrefix, className), ref: container }, rest),
      React__default.createElement("div", { className: contentClass, ref: innerRef }, React__default.Children.map(swiperItems, (child, index) => React__default.createElement("div", { className: `${classPrefix}-item-wrapper`, style: getItemStyle(index), key: index }, child))),
      renderIndicator()
    )
  );
});
Swiper.defaultProps = defaultProps;
Swiper.displayName = "NutSwiper";
const InnerSwiper = Swiper;
InnerSwiper.Item = SwiperItem;
export {
  InnerSwiper as default
};
